import { Fragment, useState, useEffect, useRef, type SyntheticEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { CalendarEvent, CalendarInput, ActivityReport, CALENDAR_SKILLS } from "@ca/shared";
import { Button, Card, CardHead, Modal, PageHead, Pbar, Ring, Select, Switch } from "@ca/ui";
import { api } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { useWrite } from "./revision-ui.tsx";
import { useToday } from "./productivity.tsx";
import { PlanVsReal } from "./report.tsx";
import { NewBadge } from "./flags.tsx";
import { ACT, fmtShort } from "./charts.tsx";
import { featureOn } from "./features.ts";
const skillNames = {
  nghe: "Luyện nghe",
  doc: "Luyện đọc",
  thithu: "Thi thử",
  nguphap: "Ngữ pháp",
  tuvung: "Từ vựng",
};
const SKILL_BARS: [string, string][] = [
  ["nghe", "Nghe"],
  ["doc", "Đọc"],
  ["thithu", "Thi thử"],
  ["nguphap", "Ngữ pháp"],
  ["tuvung", "Từ vựng"],
  ["triloi", "Trị lỗi"],
];
export function plusDay(date: string, n: number) {
  return new Date(Date.parse(date) + n * 86400000).toISOString().slice(0, 10);
}
export function useCalendar(uid: string, from: string, to: string) {
  return useQuery({
    queryKey: ["calendar", uid, from, to],
    queryFn: async () =>
      z
        .array(CalendarEvent)
        .parse(await api(`/v1/me/calendar?from=${from}&to=${to}`, { accountId: uid })),
  });
}
export function targetFor(skill: CalendarEvent["skill"]) {
  return {
    nghe: "practice/listening",
    doc: "practice/reading",
    thithu: featureOn("mocks") ? "mocks" : "tests",
    nguphap: "grammar",
    tuvung: "vocab",
  }[skill];
}
function hhmm(n: number) {
  return `${Math.floor(n / 60)
    .toString()
    .padStart(2, "0")}:${(n % 60).toString().padStart(2, "0")}`;
}
const PX = 56;
const FIRST_HOUR = 5;
const SKILL_TONE: Record<string, string> = {
  nghe: "blue",
  doc: "sage",
  thithu: "pink",
  nguphap: "lav",
  tuvung: "yellow",
};
const SHORT = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const weekdayOf = (d: string) => new Date(`${d}T00:00:00`).getDay();
const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
/** ISO week number (W40…) */
function weekNo(date: string) {
  const d = new Date(`${date}T00:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const first = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return (
    1 +
    Math.round(((d.getTime() - first.getTime()) / 86400000 - 3 + ((first.getUTCDay() + 6) % 7)) / 7)
  );
}
/** Lịch học tuần — `S.plan` của bản mẫu (spec 14). */
export function PlannerPage({
  uid,
  date,
  notify,
}: {
  uid: string;
  date: string;
  notify: (s: string) => void;
}) {
  const write = useWrite(uid, notify),
    createId = useRef(uuidv7()),
    navigate = useNavigate(),
    calRef = useRef<HTMLDivElement>(null);
  const today = useToday(uid);
  const monday = plusDay(date, -((weekdayOf(date) + 6) % 7)),
    [offset, setOffset] = useState(0),
    from = plusDay(monday, offset * 7),
    to = plusDay(from, 6),
    q = useCalendar(uid, from, to),
    cache = useQueryClient();
  const real = useQuery({
    queryKey: ["activity", uid, from, to],
    queryFn: async () =>
      ActivityReport.parse(await api(`/v1/me/activity?from=${from}&to=${to}`, { accountId: uid })),
    refetchInterval: 30000,
  });
  const [edit, setEdit] = useState<CalendarEvent | null>(null),
    [open, setOpen] = useState(false),
    [remove, setRemove] = useState(false),
    [form, setForm] = useState({
      date,
      startMin: 1200,
      endMin: 1260,
      title: "Buổi học TOEIC",
      skill: "doc" as CalendarEvent["skill"],
      done: false,
    }),
    [busy, setBusy] = useState(false),
    [selection, setSelection] = useState<{ date: string; start: number } | null>(null);
  useEffect(() => {
    // Mở lịch ở khung buổi tối như bản mẫu; vẫn cuộn được từ 05:00.
    if (calRef.current) calRef.current.scrollTop = (18 - FIRST_HOUR) * (PX + 1);
  }, []);
  const startEdit = (e?: CalendarEvent, d = date, start = 1200, end = 1260) => {
    createId.current = uuidv7();
    setEdit(e ?? null);
    setForm(
      e
        ? {
            date: e.date,
            startMin: e.startMin,
            endMin: e.endMin,
            title: e.title,
            skill: e.skill,
            done: e.done,
          }
        : {
            date: d,
            startMin: start,
            endMin: end,
            title: "Buổi học TOEIC",
            skill: "doc",
            done: false,
          },
    );
    setOpen(true);
  };
  const cellAt = (x: number, y: number) => {
    const cell = document.elementFromPoint(x, y)?.closest<HTMLElement>(".cell");
    if (!cell?.dataset.date || !cell.dataset.h) return null;
    const rect = cell.getBoundingClientRect();
    const quarter = Math.min(3, Math.max(0, Math.floor(((y - rect.top) / rect.height) * 4)));
    return { date: cell.dataset.date, min: Number(cell.dataset.h) * 60 + quarter * 15 };
  };
  const week = Array.from({ length: 7 }, (_, i) => plusDay(from, i));
  const events = q.data ?? [];
  const hours = Array.from({ length: 24 - FIRST_HOUR }, (_, i) => FIRST_HOUR + i);
  const days: Record<string, number> = {};
  for (const d of real.data?.days ?? []) days[d.date] = d.activeSecs / 60;
  const totals = week.map((d) => days[d] ?? 0);
  const planned: Record<string, number> = {};
  for (const e of events) planned[e.date] = (planned[e.date] ?? 0) + e.endMin - e.startMin;
  const doneN = events.filter((e) => e.done).length;
  const wkTot = totals.reduce((a, b) => a + b, 0);
  const goalWeek = (today.data?.goalMinutes ?? 60) * 7;
  // Phân bổ kỹ năng (spec 14.2): nghe/đọc tách theo Part, trị lỗi là thanh riêng.
  const bySkill: Record<string, number> = {};
  for (const k of real.data?.skills ?? []) bySkill[k.skill] = k.activeSecs / 60;
  const maxSkill = Math.max(60, ...Object.values(bySkill));
  // Phút học hôm nay rơi ngoài các buổi đã xếp lịch (chip "ngoài lịch").
  const todayEvents = events.filter((e) => e.date === date);
  const outside = (real.data?.buckets ?? []).filter(
    (b) =>
      b.date === date &&
      b.activeSecs >= 60 &&
      !todayEvents.some((e) => b.hour * 60 >= e.startMin - 59 && b.hour * 60 < e.endMin),
  );
  const mx = Math.max(...week.map((d, i) => Math.max(planned[d] ?? 0, totals[i] ?? 0)), 60);
  const save = (ev: SyntheticEvent) => {
    ev.preventDefault();
    const parsed = CalendarInput.safeParse(form);
    if (!parsed.success) {
      notify("Chọn giờ bắt đầu/kết thúc theo bước 15 phút.");
      return;
    }
    setBusy(true);
    void write(`/v1/me/calendar/${edit?.id ?? createId.current}`, "PUT", {
      event: parsed.data,
      expectedUpdatedAt: edit?.updatedAt ?? null,
    })
      .then(async () => {
        await cache.invalidateQueries({ queryKey: ["calendar", uid] });
        setOpen(false);
      })
      .catch((err: unknown) => {
        notify(String(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const times = Array.from({ length: 96 }, (_, i) => i * 15);
  return (
    <>
      <PageHead
        space="prod"
        title="Lịch học tuần"
        desc="Kéo trên lịch để tạo buổi học, hoặc bấm ＋ Thêm buổi học. Buổi nào của hôm nay cũng có nút ▶ Bắt đầu, bấm là vào đúng bài."
      >
        <div className="row between">
          <div className="row" style={{ gap: 6 }}>
            <Button
              variant="soft"
              size="sm"
              className="btn-icon"
              aria-label="Tuần trước"
              onClick={() => {
                setOffset(offset - 1);
              }}
            >
              ‹
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={() => {
                setOffset(0);
              }}
            >
              Hôm nay
            </Button>
            <Button
              variant="soft"
              size="sm"
              className="btn-icon"
              aria-label="Tuần sau"
              onClick={() => {
                setOffset(offset + 1);
              }}
            >
              ›
            </Button>
            <b style={{ marginLeft: 6 }}>
              W{weekNo(from)} · {ddmm(from)} – {ddmm(to)}/{to.slice(0, 4)}
            </b>
          </div>
          <Button
            variant="acc"
            size="sm"
            onClick={() => {
              startEdit();
            }}
          >
            ＋ Thêm buổi học
          </Button>
        </div>
      </PageHead>
      <Card>
        <div className="row" style={{ gap: 6, marginBottom: 12 }}>
          <span className="eyebrow">Hôm nay, ngoài lịch:</span>
          {outside.length ? (
            outside.map((b) => {
              const a = ACT.find((x) => x.id === b.activity);
              return (
                <span
                  key={`${b.hour}-${b.activity}`}
                  className={`chip b${a?.c ?? 0}`}
                  style={{ color: "#fff" }}
                >
                  {a?.ic} {String(b.hour).padStart(2, "0")}:00 · {a?.name}{" "}
                  {Math.round(b.activeSecs / 60)}p
                </span>
              );
            })
          ) : (
            <span className="faint" style={{ fontSize: 12, fontWeight: 600 }}>
              chưa có
            </span>
          )}
          <span className="faint" style={{ fontSize: 12, fontWeight: 600 }}>
            tự ghi, vẫn tính vào mục tiêu
          </span>
        </div>
        <div className="tbl-wrap">
          <div className="cal" style={{ borderRadius: "20px 20px 0 0" }}>
            <div className="ch" />
            {week.map((d) => (
              <div key={d} className={`ch${d === date ? " today" : ""}`}>
                {SHORT[weekdayOf(d)]}
                <b>{d.slice(8, 10)}</b>
              </div>
            ))}
          </div>
        </div>
        <div
          className="tbl-wrap"
          ref={calRef}
          style={{ maxHeight: 6 * (PX + 1), overflowY: "auto", marginTop: 1 }}
        >
          <div
            className="cal"
            style={{ borderRadius: "0 0 20px 20px" }}
            onPointerDown={(e) => {
              const target = e.target as HTMLElement;
              if (!target.classList.contains("cell")) return;
              const at = cellAt(e.clientX, e.clientY);
              if (at) setSelection({ date: at.date, start: at.min });
            }}
            onPointerUp={(e) => {
              if (!selection) return;
              const at = cellAt(e.clientX, e.clientY);
              const end = at && at.date === selection.date ? at.min : selection.start;
              startEdit(
                undefined,
                selection.date,
                Math.min(end, selection.start),
                Math.max(end, selection.start) + (end === selection.start ? 60 : 15),
              );
              setSelection(null);
            }}
          >
            {hours.map((h) => (
              <Fragment key={h}>
                <div className="hr">{h}:00</div>
                {week.map((d) => (
                  <div
                    key={d}
                    className="cell"
                    data-date={d}
                    data-h={h}
                    aria-label={`Tạo buổi ${ddmm(d)} lúc ${h}:00`}
                  >
                    {events
                      .filter((e) => e.date === d && Math.floor(e.startMin / 60) === h)
                      .map((e) => {
                        const top = ((e.startMin % 60) / 60) * PX + 2,
                          height = Math.max(30, ((e.endMin - e.startMin) / 60) * PX - 4);
                        return (
                          <div
                            key={e.id}
                            className={`ev${e.done ? " done" : ""}`}
                            role="button"
                            tabIndex={0}
                            aria-label={`Sửa / đánh dấu xong: ${e.title}`}
                            style={{
                              top,
                              height,
                              background: `var(--t-${SKILL_TONE[e.skill] ?? "lav"})`,
                            }}
                            data-tip={`${e.title}|${hhmm(e.startMin)}–${hhmm(e.endMin)} · ${skillNames[e.skill]}|${e.done ? "✓ Đã xong" : d === date ? "Hôm nay" : d > date ? "Kế hoạch" : "Bỏ lỡ"}`}
                            onPointerDown={(ev) => {
                              ev.stopPropagation();
                            }}
                            onClick={() => {
                              startEdit(e);
                            }}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter") startEdit(e);
                            }}
                          >
                            <b>
                              {e.done ? "✓ " : ""}
                              {e.title}
                            </b>
                            {height > 40 && (
                              <span>
                                {hhmm(e.startMin)}–{hhmm(e.endMin)} · {skillNames[e.skill]}
                              </span>
                            )}
                            {!e.done && d === date && (
                              <button
                                className="go"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  void navigate({
                                    to: "/$",
                                    params: { _splat: targetFor(e.skill) },
                                  });
                                }}
                              >
                                ▶ Bắt đầu
                              </button>
                            )}
                            {e.done && <span className="evp" style={{ width: "100%" }} />}
                          </div>
                        );
                      })}
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
        <p className="faint" style={{ fontSize: 12.5, marginTop: 10, fontWeight: 600 }}>
          {events.length
            ? "Kéo trên một cột để tạo buổi theo bước 15 phút; bấm một buổi để sửa hoặc đánh dấu xong. Lịch cuộn được 05:00–24:00."
            : "Kéo trên lịch để tạo buổi học."}
        </p>
      </Card>
      <div className="grid g3">
        <Card className="stack">
          <h3>Tỉ lệ hoàn thành</h3>
          <div className="row" style={{ gap: 16 }}>
            <div style={{ width: 92, height: 92 }}>
              <Ring
                pct={events.length ? (doneN / events.length) * 100 : 0}
                sw={12}
                color="var(--accent-2)"
              />
            </div>
            <div>
              <b style={{ fontSize: 26 }}>
                {events.length ? Math.round((doneN / events.length) * 100) : 0}%
              </b>
              <p className="muted" style={{ fontSize: 13 }}>
                {doneN}/{events.length} buổi trong tuần đã xong. Giữ trên 70% là đủ nhịp để lên
                điểm.
              </p>
            </div>
          </div>
        </Card>
        <Card className="stack">
          <h3>Tổng giờ học tuần này</h3>
          <b style={{ fontSize: 30, letterSpacing: "-.02em" }}>
            {fmtShort(wkTot)}{" "}
            <span className="faint" style={{ fontSize: 15 }}>
              / {fmtShort(goalWeek)}
            </span>
          </b>
          <Pbar pct={(wkTot / goalWeek) * 100} style={{ height: 8 }} />
          <p className="muted" style={{ fontSize: 13 }}>
            Lấy từ giờ tự ghi (thực tế), không cộng từ lịch, nên luôn đúng với những gì bạn đã học.
          </p>
        </Card>
        <Card>
          <h3 style={{ marginBottom: 8 }}>Phân bổ kỹ năng</h3>
          {SKILL_BARS.map(([id, name]) => (
            <div
              key={id}
              className="hbar"
              style={{ gridTemplateColumns: "72px minmax(0,1fr) 44px", padding: "4px 0" }}
            >
              <span className="hl">{name}</span>
              <span className="ht">
                <i className="b1" style={{ width: `${((bySkill[id] ?? 0) / maxSkill) * 100}%` }} />
              </span>
              <span className="hv">{fmtShort(bySkill[id] ?? 0)}</span>
            </div>
          ))}
        </Card>
      </div>
      <Card>
        <CardHead
          title={
            <>
              Kế hoạch so với thực tế <NewBadge />
            </>
          }
          sub="Thấy ngay ngày nào học hụt so với lịch đã đặt"
        />
        <PlanVsReal week={week} date={date} planned={planned} totals={totals} mx={mx} />
      </Card>
      {q.isError && <p role="alert">{String(q.error)}</p>}
      {!!events.length && <PushToggle uid={uid} notify={notify} />}
      {open && (
        <Modal
          title={edit ? "Sửa buổi học" : "Thêm buổi học"}
          head={
            <div>
              <h2>{edit ? "Sửa buổi học" : "＋ Thêm buổi học"}</h2>
              <p className="muted" style={{ fontSize: 13.5 }}>
                Buổi học trong lịch sẽ hiện ở “Kế hoạch tối nay” và có lời nhắc trước 5 phút.
              </p>
            </div>
          }
          onClose={() => {
            setOpen(false);
          }}
        >
          <form className="stack" onSubmit={save}>
            <div className="field">
              <label htmlFor="caT">Tên buổi</label>
              <input
                id="caT"
                className="input"
                required
                maxLength={150}
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value });
                }}
              />
            </div>
            <div className="grid g3">
              <div className="field">
                <label>Ngày</label>
                <Select
                  label="Ngày"
                  value={form.date}
                  options={Array.from({ length: 14 }, (_, i) => plusDay(from, i - 0)).map((d) => ({
                    value: d,
                    label: `${SHORT[weekdayOf(d)]} ${ddmm(d)}`,
                  }))}
                  onChange={(v) => {
                    setForm({ ...form, date: v });
                  }}
                />
              </div>
              <div className="field">
                <label>Bắt đầu</label>
                <Select
                  label="Bắt đầu"
                  value={form.startMin}
                  options={times.map((t) => ({ value: t, label: hhmm(t) }))}
                  onChange={(v) => {
                    setForm({
                      ...form,
                      startMin: v,
                      endMin: Math.min(1440, v + (form.endMin - form.startMin)),
                    });
                  }}
                />
              </div>
              <div className="field">
                <label>Thời lượng</label>
                <Select
                  label="Thời lượng"
                  value={form.endMin - form.startMin}
                  options={[15, 30, 45, 60, 75, 90, 120, 150, 180].map((m) => ({
                    value: m,
                    label: `${m} phút`,
                  }))}
                  onChange={(v) => {
                    setForm({ ...form, endMin: Math.min(1440, form.startMin + v) });
                  }}
                />
              </div>
            </div>
            <div className="field">
              <label>Loại việc</label>
              <Select
                label="Việc học"
                value={form.skill}
                options={CALENDAR_SKILLS.map((s) => ({ value: s, label: skillNames[s] }))}
                onChange={(v) => {
                  setForm({ ...form, skill: v });
                }}
              />
            </div>
            {edit && (
              <div className="row between">
                <span>Đã hoàn thành</span>
                <Switch
                  label="Đã hoàn thành"
                  checked={form.done}
                  onChange={(v) => {
                    setForm({ ...form, done: v });
                  }}
                />
              </div>
            )}
            <Button type="submit" variant="acc" size="lg" disabled={busy}>
              {edit ? "Lưu buổi học" : "Thêm vào lịch"}
            </Button>
            {edit && (
              <Button
                variant="line"
                disabled={busy}
                onClick={() => {
                  setRemove(true);
                }}
              >
                Xoá buổi học
              </Button>
            )}
          </form>
        </Modal>
      )}
      {remove && edit && (
        <Modal
          title="Xoá buổi học"
          className="sm"
          onClose={() => {
            setRemove(false);
          }}
        >
          <p className="muted">“{edit.title}” sẽ bị xoá khỏi lịch.</p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setRemove(false);
              }}
            >
              Huỷ
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void api(`/v1/me/calendar/${edit.id}`, { accountId: uid, method: "DELETE" })
                  .then(async () => {
                    await cache.invalidateQueries({ queryKey: ["calendar", uid] });
                    setRemove(false);
                    setOpen(false);
                  })
                  .catch((err: unknown) => {
                    notify(String(err));
                  })
                  .finally(() => {
                    setBusy(false);
                  });
              }}
            >
              🗑 Xoá
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
function PushToggle({ uid, notify }: { uid: string; notify: (s: string) => void }) {
  const [busy, setBusy] = useState(false),
    [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if ("serviceWorker" in navigator)
      void navigator.serviceWorker.ready
        .then((r) => r.pushManager.getSubscription())
        .then((s) => {
          setEnabled(!!s);
        })
        .catch(() => {});
  }, []);
  return (
    <Card tight className="row between">
      <div>
        <h3>🔔 Nhắc lịch</h3>
        <p className="sub">Nhận thông báo trước giờ học 5 phút trên trình duyệt này.</p>
      </div>
      <Button
        variant="soft"
        size="sm"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void (async () => {
            if (!("serviceWorker" in navigator) || !("PushManager" in window))
              throw new Error("Trình duyệt này chưa hỗ trợ nhắc lịch.");
            if (!enabled) {
              const permission = await Notification.requestPermission();
              if (permission !== "granted")
                throw new Error(
                  "Chưa có quyền thông báo. Bạn có thể bật lại trong cài đặt trình duyệt.",
                );
            }
            await navigator.serviceWorker.register("/sw.js");
            const registration = await navigator.serviceWorker.ready,
              existing = await registration.pushManager.getSubscription();
            if (enabled && existing) {
              await api("/v1/me/push", {
                accountId: uid,
                method: "DELETE",
                body: { endpoint: existing.endpoint },
              });
              await existing.unsubscribe();
              setEnabled(false);
              return;
            }
            const config = z
              .object({ publicKey: z.string().nullable() })
              .parse(await api("/v1/me/push/config", { accountId: uid }));
            if (!config.publicKey) throw new Error("Nhắc lịch cần cấu hình VAPID trên máy chủ.");
            const bytes = Uint8Array.from(
              atob(config.publicKey.replace(/-/g, "+").replace(/_/g, "/")),
              (c) => c.charCodeAt(0),
            );
            const sub =
              existing ??
              (await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: bytes,
              }));
            const json = sub.toJSON();
            await api("/v1/me/push", {
              accountId: uid,
              method: "PUT",
              body: { endpoint: sub.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth },
            });
            setEnabled(true);
            notify("Đã bật nhắc lịch trên trình duyệt này.");
          })()
            .catch((err: unknown) => {
              notify(String(err));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        {enabled ? "Tắt nhắc lịch" : "Bật nhắc lịch"}
      </Button>
    </Card>
  );
}

export async function revokeLocalPush(uid: string) {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const sub = await registration?.pushManager.getSubscription();
  if (!sub) return;
  await sub.unsubscribe();
  await api("/v1/me/push", { accountId: uid, method: "DELETE", body: { endpoint: sub.endpoint } });
}
